// Side-effect import: registers the <foundry-sidebar-chatbot> custom element.
import "@ai-foundry/sdk/ui/foundry-sidebar-chatbot";

import { AfterViewInit, Component, inject, NgZone, OnDestroy } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { getLoggerFor } from "@tylertech/logging";
import { createFetchHttpPlugin, ensureSmartPulseFramework } from "@tylertech/smart-pulse-core";
import {
    createDOMPolicyForgeDriverPlugin,
    createFrameworkMenuForgeDriverPlugin,
    createInlineControlsForgeDriverPlugin,
} from "@tylertech/smart-pulse-forge-ui";
import {
    createPageGraphGenericForgeDriverPlugin,
    createPageGraphPlugin,
} from "@tylertech/smart-pulse-page-graph";
import {
    createRedactionPolicyDefaultDriverPlugin,
    createRedactionPolicyPlugin,
} from "@tylertech/smart-pulse-redaction-policy";
import { createUserActivityPlugin } from "@tylertech/smart-pulse-user-activity";
import { ChatSidebarApi, createChatSidebarPlugin } from "@tylertech/smart-pulse-foundry-chat-sidebar";
import { distinctUntilChanged, filter, map, startWith } from "rxjs";
import { Agent, AiAgentService, FeatureFlagHelperService, FeatureFlags, createCxEscalationPlugin, CxBridgeService, FoundryChatAdapter, ReleaseStage, SecurityService, TylerAssistantSidebarService } from "../../../services";
import { createFollowMePlugin } from "./followMe.plugin";
import { publishRightPanelWidth } from "./rightPanelWidth";

const DRAWER_OPEN_KEY = "sp-drawer-open";
const THREAD_KEY = "sp-thread-id";

@Component({
    selector: "smart-pulse",
    standalone: false,
    templateUrl: "./smartPulse.tmpl.html"
})
export class SmartPulseComponent implements AfterViewInit, OnDestroy
{
    private readonly $aiAgentService = inject(AiAgentService);
    private readonly $securityService = inject(SecurityService);
    private readonly $cxBridgeService = inject(CxBridgeService);
    private readonly $foundryChatAdapter = inject(FoundryChatAdapter);
    private readonly $featureFlagHelperService = inject(FeatureFlagHelperService);
    private readonly $router = inject(Router);
    private readonly $ngZone = inject(NgZone);
    private readonly $tylerAssistantSidebarService = inject(TylerAssistantSidebarService);
    protected readonly sidebar = this.$tylerAssistantSidebarService.sidebar;

    private readonly log = getLoggerFor("SmartPulseComponent");
    private wafRetryHandle?: ReturnType<typeof setTimeout>;
    private wafRetriesRemaining = 20;
    private stopPublishingDrawerWidth?: () => void;

    constructor()
    {
        this.$tylerAssistantSidebarService.reconnectRequested
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.reconnect());
    }

    async ngAfterViewInit(): Promise<void>
    {
        if (this.sidebar() != null)
            return;
        performance.mark("smart-pulse:fetch-start");
        try
        {
            await this.$aiAgentService.fetchAgents();
        }
        catch (error)
        {
            this.log.error("Failed to fetch AI agents", error);
            return;
        }
        performance.measure("smart-pulse:fetch", "smart-pulse:fetch-start");
        const agent = this.$aiAgentService.currentAgent;
        if (!agent)
            return;

        const sidebarContainer = document.querySelector<HTMLElement>("forge-scaffold")
            ?? document.body;

        await this.$featureFlagHelperService.whenReady;
        const followMeEnabled = this.$featureFlagHelperService.get(FeatureFlags.Shared_All_SmartPulseFollowMe)();

        performance.mark("smart-pulse:launch-start");
        this.launch(agent, sidebarContainer, followMeEnabled);
        performance.measure("smart-pulse:launch", "smart-pulse:launch-start");
    }

    toggleSidebar(): void
    {
        if (this.$tylerAssistantSidebarService.foundryUnavailable())
        {
            this.$tylerAssistantSidebarService.fallbackOpen.update(open => !open);
            return;
        }

        const sidebar = this.sidebar();
        if (!sidebar)
            return;

        if (sidebar.isOpen())
            sidebar.close();
        else
            sidebar.open();
    }

    private applyLocalWafWorkaround(api: ChatSidebarApi): void
    {
        if (!this.$aiAgentService.isLocal)
            return;

        const component = api.getComponent();
        if (!component)
        {
            if (this.wafRetriesRemaining-- <= 0)
                return;
            this.wafRetryHandle = setTimeout(() => this.applyLocalWafWorkaround(api), 250);
            return;
        }

        const originalSetContext = component.setContext.bind(component);
        component.setContext = (ctx: Record<string, unknown>) =>
            originalSetContext({ ...ctx, pageUrl: `${location.host}${location.pathname}` });
        component.setContext({});
    }

    private attachCxBridge(agent: Agent, api: ChatSidebarApi): void
    {
        if (!agent.EnableClientBridge)
            return;

        const component = api.getComponent();
        if (!component)
        {
            this.log.error("Chat sidebar component not found - CX escalation bridge not attached.");
            return;
        }

        // The sidebar element shares the floating embed's API surface (see foundryChatbot.ts);
        // all direct element access stays contained in FoundryChatAdapter. The escalate
        // tool itself is contributed by createCxEscalationPlugin, not attached here.
        this.$foundryChatAdapter.attach(component);
    }

    private restoreDrawerOpenState(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component)
            return;

        component.open = sessionStorage.getItem(DRAWER_OPEN_KEY) === "true";
        component.addEventListener("foundry-sidebar-chatbot-open", () => sessionStorage.setItem(DRAWER_OPEN_KEY, "true"));
        component.addEventListener("foundry-sidebar-chatbot-close", () => sessionStorage.setItem(DRAWER_OPEN_KEY, "false"));
    }

    private restoreThread(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component)
            return;

        const assignThread = (threadId: string): void =>
        {
            sessionStorage.setItem(THREAD_KEY, threadId);
            component.threadId = threadId;
        };

        assignThread(sessionStorage.getItem(THREAD_KEY) ?? crypto.randomUUID());
        component.addEventListener("foundry-chatbot-new-chat", () => assignThread(crypto.randomUUID()));
        component.addEventListener("foundry-chatbot-cleared", () => assignThread(crypto.randomUUID()));
    }

    private publishDrawerWidth(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component) return;
        this.stopPublishingDrawerWidth = publishRightPanelWidth(component);
    }

    private watchForFoundryOutage(api: ChatSidebarApi): void
    {
        const component = api.getComponent();
        if (!component) return;
        component.addEventListener("foundry-chatbot-error", event =>
        {
            this.log.warning("Tyler Assistant Unavailable", event.detail.error.code, event.detail.error.message);
            this.$tylerAssistantSidebarService.fallbackOpen.set(component.open);
            this.$tylerAssistantSidebarService.foundryUnavailable.set(true);
            component.style.display = "none";
        });
    }

    private wireSidebar(agent: Agent, api: ChatSidebarApi): void
    {
        this.applyLocalWafWorkaround(api);
        this.attachCxBridge(agent, api);
        this.restoreDrawerOpenState(api);
        this.restoreThread(api);
        this.publishDrawerWidth(api);
        this.watchForFoundryOutage(api);
    }

    /** Replaces the failed chat element with a fresh one; the Smart Pulse framework itself is a global singleton and stays up. */
    private reconnect(): void
    {
        const sidebar = this.sidebar();
        const agent = this.$aiAgentService.currentAgent;
        if (!sidebar || !agent)
            return;

        this.$tylerAssistantSidebarService.foundryUnavailable.set(false);
        this.$tylerAssistantSidebarService.fallbackOpen.set(false);
        this.$ngZone.runOutsideAngular(() =>
        {
            this.stopPublishingDrawerWidth?.();
            sidebar.dispose();
            sidebar.init();
            this.wireSidebar(agent, sidebar);
            sidebar.open();
        });
    }

    private launch(agent: Agent, sidebarContainer: HTMLElement, followMeEnabled: boolean): void
    {
        const plugins = [
            createDOMPolicyForgeDriverPlugin({}),
            createInlineControlsForgeDriverPlugin(),
            createFrameworkMenuForgeDriverPlugin(),
            createFetchHttpPlugin(),
            createPageGraphGenericForgeDriverPlugin({ includeFieldValues: true }),
            createPageGraphPlugin({ registerAsContextProvider: true }),
            createRedactionPolicyDefaultDriverPlugin({ showLastFourMasked: true }),
            createUserActivityPlugin({ includeFieldValues: true, maxActivityEntries: 10, registerAsContextProvider: true }),
            // Must precede createChatSidebarPlugin: the sidebar collects tool
            // definitions once, in its afterInit, and afterInit hooks run in array order.
            ...(agent.EnableClientBridge ? [createCxEscalationPlugin(this.$cxBridgeService)] : []),
            createChatSidebarPlugin(
                {
                    baseUrl: this.$aiAgentService.foundryBaseUrl(agent.ReleaseStage),
                    chatButtonLabel: agent.Description ?? "Tyler Assistant",
                    chatButtonTooltip: "",
                    defaultAgentId: agent.AgentId,
                    sidebarContainer,
                    pickerAgents: [],
                },
                api =>
                {
                    this.$tylerAssistantSidebarService.sidebar.set(api);
                    this.wireSidebar(agent, api);
                }),
        ];

        if (followMeEnabled)
        {
            plugins.push(createFollowMePlugin(this.$foundryChatAdapter.onLiveModeChanged, followMe =>
                this.$router.events
                    .pipe(
                        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                        map(event => event.urlAfterRedirects.split(/[?#]/)[0]),
                        startWith(this.$router.url.split(/[?#]/)[0]),
                        distinctUntilChanged())
                    .subscribe(() => followMe.noteNavigation())));
        }

        try
        {
            this.$ngZone.runOutsideAngular(() => ensureSmartPulseFramework({
                hostAppId: "tyl_erp_pro10", // TODO: derive per app instead of hardcoding
                hostAppName: "ERP Pro 10",  // TODO: derive per app instead of hardcoding
                userId: this.$securityService.principle?.Email ?? "",
                // Best effort: Smart Pulse only forwards env to plugin settings and logger
                env: agent.ReleaseStage === ReleaseStage.Staging ? "test" : "prod",
                settings: {},
                plugins,
            }));
        }
        catch (error)
        {
            this.log.error("Smart Pulse failed to launch", error);
        }
    }

    ngOnDestroy(): void
    {
        clearTimeout(this.wafRetryHandle);

        if (this.$securityService.isAuthenticated)
            return;

        sessionStorage.removeItem(THREAD_KEY);
        this.sidebar()?.close();
    }
}
